// src/components/ListasTrabajo/GestionListas/DetalleListaDialog.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Checkbox,
    TextField,
    Box,
    Typography,
    Chip,
    CircularProgress,
    Tooltip,
    Snackbar,
    Alert as MuiAlert
} from '@mui/material';
import {
    Close as CloseIcon,
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
    Edit as EditIcon,
    Save as SaveIcon,
    Warning as WarningIcon,
    FileDownload as DownloadIcon,
    DragIndicator as DragIcon,
} from '@mui/icons-material';
import { formatDotNetDate } from '../../../utils/dateHelpers';
import listasTrabajoService from '../../../services/listasTrabajoService';
import { listasTrabajoReordenarJWT } from '../../../api/endpoints';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import AccionesMasivasDialog from './AccionesMasivasDialog';

const DetalleListaDialog = ({ open, onClose, lista, onExpedientesEliminados, onSuccess, usuario }) => {
    const [expedientes, setExpedientes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [seleccionados, setSeleccionados] = useState([]);
    const [cargandoTodos, setCargandoTodos] = useState(false);
    const [editandoNotas, setEditandoNotas] = useState({});
    const [notasTemp, setNotasTemp] = useState({});

    const listaId = lista ? lista.Id : null;
    const totalExp = lista?.Total_Expedientes || 0;
    const PAGE_SIZE = 50;
    const usePagination = totalExp > PAGE_SIZE;

    // Paginación
    const [page, setPage] = useState(1);
    const totalPages = usePagination ? Math.ceil(totalExp / PAGE_SIZE) : 1;

    const [dialogAccionesMasivas, setDialogAccionesMasivas] = useState(false);
    const [eliminarDialog, setEliminarDialog] = useState({ open: false, mensaje: '', onConfirm: null });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Evita setState despues del unmount
    const aliveRef = useRef(true);
    useEffect(() => { aliveRef.current = true; return () => { aliveRef.current = false; }; }, []);

    // Cache de expedientes ya vistos (por Id_Expediente). Acumula las páginas
    // visitadas + el fetch de "seleccionar todos". Sirve para pasarle a
    // Acciones Masivas los datos completos de TODOS los seleccionados, no solo
    // los de la página visible.
    const expedientesCacheRef = useRef(new Map());

    const cargarExpedientes = useCallback(async (pg) => {
        if (!listaId) return;

        setLoading(true);
        try {
            const currentPage = pg || page;
            const response = await listasTrabajoService.obtenerLista(listaId, usePagination ? currentPage : undefined, usePagination ? PAGE_SIZE : undefined);
            const exps = response?.expedientes || [];
            if (!aliveRef.current) return;
            setExpedientes(Array.isArray(exps) ? exps : []);
        } catch (error) {
            console.error('Error al cargar expedientes:', error);
            if (aliveRef.current) {
                setSnackbar({ open: true, message: 'Error al cargar expedientes: ' + error.message, severity: 'error' });
                setExpedientes([]);
            }
        } finally {
            if (aliveRef.current) setLoading(false);
        }
    }, [listaId, page, usePagination]);

    useEffect(() => {
        if (open && listaId) {
            setExpedientes([]);
            setPage(1);
            setSeleccionados([]);
            setEditandoNotas({});
            setNotasTemp({});
            expedientesCacheRef.current = new Map(); // reset cache al cambiar de lista
        }
    }, [open, listaId]);

    useEffect(() => {
        if (open && listaId) {
            cargarExpedientes(page);
        }
    }, [open, listaId, page, cargarExpedientes]);

    // Cachear los expedientes de la página actual a medida que se van cargando.
    useEffect(() => {
        (expedientes || []).forEach(e => {
            if (e && e.Id_Expediente != null) expedientesCacheRef.current.set(e.Id_Expediente, e);
        });
    }, [expedientes]);

    // Al cambiar de página NO borramos la selección — el usuario puede ir
    // tildando expedientes de varias páginas y mandarlos todos juntos a
    // acciones masivas. (Antes se perdía lo seleccionado al paginar.)
    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handleExportarExcel = () => {
        if (!expedientesArray.length) return;
        const esAdmin = lista?.Tipo_Expediente === 2;

        const headers = esAdmin
            ? ['N° Expediente', 'Extracto', 'Iniciador', 'Estado', 'Agregado', 'Notas']
            : ['Carátula', 'Autos', 'Estado', 'Agregado', 'Notas'];

        const rows = expedientesArray.map(exp => {
            if (esAdmin) {
                return {
                    'N° Expediente': exp.Expediente_Adm || exp.Autos || '',
                    'Extracto': exp.Extracto || exp.Caratula || '',
                    'Iniciador': exp.Iniciador || '',
                    'Estado': exp.Estado || '',
                    'Agregado': formatDotNetDate(exp.Fecha_Agregado, 'dd/MM/yyyy') || '',
                    'Notas': exp.Notas || '',
                };
            }
            return {
                'Carátula': (exp.Caratula || '').toLowerCase().replace(/\b\w/g, c => c.toUpperCase()),
                'Autos': exp.Autos || '',
                'Estado': exp.Estado || '',
                'Agregado': formatDotNetDate(exp.Fecha_Agregado, 'dd/MM/yyyy') || '',
                'Notas': exp.Notas || '',
            };
        });

        const ws = XLSX.utils.json_to_sheet(rows, { header: headers });
        // Auto-width columns
        ws['!cols'] = headers.map(h => ({ wch: Math.max(h.length, 15) }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Expedientes');
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `${lista?.Nombre || 'lista'}_expedientes.xlsx`);
    };

    // Drag & drop
    const dragItem = useRef(null);
    const dragOverItem = useRef(null);

    const handleDragStart = (index) => {
        dragItem.current = index;
    };

    const handleDragEnter = (index) => {
        dragOverItem.current = index;
    };

    const handleDragEnd = async () => {
        if (dragItem.current === null || dragOverItem.current === null || dragItem.current === dragOverItem.current) return;
        const items = [...expedientesArray];
        const draggedItem = items[dragItem.current];
        items.splice(dragItem.current, 1);
        items.splice(dragOverItem.current, 0, draggedItem);
        setExpedientes(items);
        dragItem.current = null;
        dragOverItem.current = null;

        // Guardar orden en backend
        const orden = items.map((exp, i) => ({ idExpediente: exp.Id_Expediente, orden: i + 1 }));
        try {
            await listasTrabajoReordenarJWT(lista.Id, orden);
        } catch {
            // Si falla, recargar
            cargarExpedientes(page);
        }
    };

    // Seleccionar todos: si la lista está paginada, traemos TODOS los expedientes
    // (no solo la página visible) para poder mandarlos a acciones masivas.
    const handleSeleccionarTodos = async (event) => {
        if (!event.target.checked) {
            setSeleccionados([]);
            return;
        }
        if (!usePagination) {
            setSeleccionados((expedientes || []).map(exp => exp.Id_Expediente));
            return;
        }
        // Lista paginada: pedir todos los expedientes sin paginar para sus IDs.
        setCargandoTodos(true);
        try {
            const resp = await listasTrabajoService.obtenerLista(listaId);
            const todosExp = resp?.expedientes || [];
            // Cacheamos los datos completos para poder pasarlos a Acciones Masivas.
            todosExp.forEach(e => {
                if (e && e.Id_Expediente != null) expedientesCacheRef.current.set(e.Id_Expediente, e);
            });
            setSeleccionados(todosExp.map(exp => exp.Id_Expediente));
        } catch (error) {
            setSnackbar({ open: true, message: 'No se pudieron seleccionar todos: ' + error.message, severity: 'error' });
        } finally {
            setCargandoTodos(false);
        }
    };

    const handleSeleccionar = (idExpediente) => {
        setSeleccionados(prev => {
            if (prev.includes(idExpediente)) {
                return prev.filter(id => id !== idExpediente);
            } else {
                return [...prev, idExpediente];
            }
        });
    };

    const handleEliminarSeleccionados = () => {
        if (seleccionados.length === 0) {
            setSnackbar({ open: true, message: 'Selecciona al menos un expediente', severity: 'warning' });
            return;
        }

        setEliminarDialog({
            open: true,
            mensaje: `¿Eliminar ${seleccionados.length} expediente(s) de la lista?`,
            onConfirm: async () => {
                setEliminarDialog(prev => ({ ...prev, open: false }));
                try {
                    await listasTrabajoService.eliminarExpedientes(lista.Id, seleccionados);
                    setExpedientes(prev => (prev || []).filter(exp => !seleccionados.includes(exp.Id_Expediente)));
                    setSeleccionados([]);
                    if (onExpedientesEliminados) onExpedientesEliminados(seleccionados.length);
                    setSnackbar({ open: true, message: 'Expedientes eliminados de la lista', severity: 'success' });
                } catch (error) {
                    console.error('Error al eliminar expedientes:', error);
                    setSnackbar({ open: true, message: 'Error al eliminar expedientes: ' + error.message, severity: 'error' });
                }
            }
        });
    };

    const handleVerExpediente = async (expediente) => {
        try {
            // 1. Abrir expediente en nueva ventana
            const tipoExp = expediente.Tipo_Expediente || lista.Tipo_Expediente || 1;
            const tipoRuta = tipoExp === 1 ? 'judicial' : 'administrativo';
            const url = `/expediente/${tipoRuta}/${expediente.Id_Expediente}`;
            window.open(url, '_blank', 'noopener,noreferrer');

            // 2. Marcar como visto (si no lo esta)
            if (!expediente.Visto) {
                await listasTrabajoService.marcarComoVisto(lista.Id, expediente.Id_Expediente);

                // 3. Actualizar local con resaltado
                setExpedientes(prev => {
                    const actualizado = (prev || []).map(exp =>
                        exp.Id_Expediente === expediente.Id_Expediente
                            ? { ...exp, Visto: true, Fecha_Visto: new Date().toISOString() }
                            : exp
                    );
                    return actualizado;
                });

                // 4. Notificar al componente padre para que recargue
                if (onSuccess) {
                    onSuccess();
                }
            }
        } catch (error) {
            console.error('Error al ver expediente:', error);
        }
    };

    const handleEditarNotas = (idExpediente, notasActuales) => {
        setEditandoNotas(prev => ({ ...prev, [idExpediente]: true }));
        setNotasTemp(prev => ({ ...prev, [idExpediente]: notasActuales || '' }));
    };

    const handleGuardarNotas = async (idExpediente) => {
        try {
            const notas = notasTemp[idExpediente] || '';
            await listasTrabajoService.actualizarNotas(lista.Id, idExpediente, notas);

            // Actualizar local
            setExpedientes(prev => (prev || []).map(exp =>
                exp.Id_Expediente === idExpediente
                    ? { ...exp, Notas: notas }
                    : exp
            ));

            setEditandoNotas(prev => ({ ...prev, [idExpediente]: false }));

        } catch (error) {
            console.error('Error al guardar notas:', error);
            setSnackbar({ open: true, message: 'Error al guardar notas: ' + error.message, severity: 'error' });
        }
    };

    const handleCancelarNotas = (idExpediente) => {
        setEditandoNotas(prev => ({ ...prev, [idExpediente]: false }));
        setNotasTemp(prev => ({ ...prev, [idExpediente]: '' }));
    };

    if (!lista) return null;

    const expedientesArray = Array.isArray(expedientes) ? expedientes : [];
    const totalExpedientes = expedientesArray.length;
    const expedientesVistos = expedientesArray.filter(exp => exp.Visto).length;
    const porcentajeVisto = totalExpedientes > 0
        ? Math.round((expedientesVistos / totalExpedientes) * 100)
        : 0;

    return (
        <>
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{ sx: { minHeight: '80vh', maxHeight: '80vh' } }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2, pr: 1, py: 1.5 }}>
                {/* Título */}
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
                        {lista.Nombre}
                    </Typography>
                    {lista.Descripcion && (
                        <Typography variant="caption" color="text.secondary" noWrap component="div">
                            {lista.Descripcion}
                        </Typography>
                    )}
                </Box>

                {/* Stats cards centrados */}
                <Box sx={{ display: 'flex', gap: 1, flex: 1, justifyContent: 'center' }}>
                    <Box sx={{ p: 0.75, borderRadius: '4px', bgcolor: '#f8f9fa', border: '1px solid #e6edf3', textAlign: 'center', minWidth: 70 }}>
                        <Typography sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1, color: '#1769aa' }}>{totalExpedientes}</Typography>
                        <Typography sx={{ fontSize: 9, color: '#6b6b6b', fontWeight: 500, mt: 0.3 }}>Total</Typography>
                    </Box>
                    <Box sx={{ p: 0.75, borderRadius: '4px', bgcolor: '#f8f9fa', border: '1px solid #e6edf3', textAlign: 'center', minWidth: 70 }}>
                        <Typography sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1, color: '#4caf50' }}>{expedientesVistos}</Typography>
                        <Typography sx={{ fontSize: 9, color: '#6b6b6b', fontWeight: 500, mt: 0.3 }}>Vistos</Typography>
                    </Box>
                    <Box sx={{ p: 0.75, borderRadius: '4px', bgcolor: '#f8f9fa', border: '1px solid #e6edf3', textAlign: 'center', minWidth: 70 }}>
                        <Typography sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1, color: porcentajeVisto === 100 ? '#4caf50' : '#ff9800' }}>{porcentajeVisto}%</Typography>
                        <Typography sx={{ fontSize: 9, color: '#6b6b6b', fontWeight: 500, mt: 0.3 }}>Progreso</Typography>
                    </Box>
                </Box>

                {/* Botones de acción */}
                {expedientesArray.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                        <Tooltip title="Exportar Excel">
                            <IconButton size="small" onClick={handleExportarExcel} sx={{ border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                                <DownloadIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Button
                            variant="contained"
                            size="small"
                            disabled={seleccionados.length === 0}
                            onClick={() => setDialogAccionesMasivas(true)}
                            sx={{ textTransform: 'none', borderRadius: '4px', whiteSpace: 'nowrap' }}
                        >
                            Acciones Masivas ({seleccionados.length})
                        </Button>
                    </Box>
                )}

                <IconButton onClick={onClose} size="small" sx={{ flexShrink: 0 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 2 }}>

                {/* Seleccionados + eliminar */}
                {seleccionados.length > 0 && (
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Typography variant="body2" color="text.secondary">
                            {seleccionados.length} seleccionado(s)
                        </Typography>
                        <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={<DeleteIcon />}
                            onClick={handleEliminarSeleccionados}
                            sx={{ textTransform: 'none', borderRadius: '4px' }}
                        >
                            Eliminar
                        </Button>
                    </Box>
                )}

                {/* Tabla de expedientes */}
                {loading ? (
                    <Box display="flex" justifyContent="center" p={4}>
                        <CircularProgress />
                    </Box>
                ) : expedientesArray.length === 0 ? (
                    <Box sx={{ p: 6, textAlign: 'center' }}>
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            No hay expedientes en esta lista
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Agrega expedientes desde la busqueda avanzada
                        </Typography>
                    </Box>
                ) : (
                    <>
                    <TableContainer component={Paper} sx={{ maxHeight: 'calc(80vh - 120px)', mt: 1 }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ width: 28, p: 0 }} />
                                    <TableCell padding="checkbox">
                                        {/* checked/indeterminate contra el TOTAL real de la lista
                                            (totalExp), no la pagina visible — asi refleja si estan
                                            TODOS seleccionados aunque haya varias paginas. */}
                                        <Checkbox
                                            disabled={cargandoTodos}
                                            checked={seleccionados.length >= (usePagination ? totalExp : totalExpedientes) && (usePagination ? totalExp : totalExpedientes) > 0}
                                            indeterminate={seleccionados.length > 0 && seleccionados.length < (usePagination ? totalExp : totalExpedientes)}
                                            onChange={handleSeleccionarTodos}
                                        />
                                    </TableCell>
                                            {lista.Tipo_Expediente === 2 ? (
                                                <>
                                                    <TableCell>N° Expediente</TableCell>
                                                    <TableCell>Extracto</TableCell>
                                                    <TableCell>Iniciador</TableCell>
                                                    <TableCell>Estado</TableCell>
                                                    <TableCell>Agregado</TableCell>
                                                    <TableCell>Notas</TableCell>
                                                    <TableCell align="center">Acciones</TableCell>
                                                </>
                                            ) : (
                                                <>
                                                    <TableCell>Caratula</TableCell>
                                                    <TableCell>Autos</TableCell>
                                                    <TableCell>Estado</TableCell>
                                                    <TableCell>Agregado</TableCell>
                                                    <TableCell>Notas</TableCell>
                                                    <TableCell align="center">Acciones</TableCell>
                                                </>
                                            )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {expedientesArray.map((expediente, idx) => (
                                    <TableRow
                                        key={expediente.Id_Expediente}
                                        draggable
                                        onDragStart={() => handleDragStart(idx)}
                                        onDragEnter={() => handleDragEnter(idx)}
                                        onDragEnd={handleDragEnd}
                                        onDragOver={(e) => e.preventDefault()}
                                        sx={{
                                            backgroundColor: expediente.Visto
                                                ? '#e8f5e9 !important'
                                                : (idx % 2 === 0 ? 'rgba(0,0,0,0.02)' : 'transparent'),
                                            borderLeft: expediente.Visto
                                                ? '4px solid #4caf50'
                                                : '4px solid transparent',
                                            transition: 'all 0.3s ease',
                                            cursor: 'grab',
                                            '&:active': { cursor: 'grabbing' },
                                            '&:hover': {
                                                backgroundColor: expediente.Visto
                                                    ? '#c8e6c9 !important'
                                                    : '#f5f5f5 !important'
                                            }
                                        }}
                                    >
                                        <TableCell sx={{ width: 28, p: 0.5, cursor: 'grab' }}>
                                            <DragIcon sx={{ fontSize: 18, color: '#bdbdbd', verticalAlign: 'middle' }} />
                                        </TableCell>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={seleccionados.includes(expediente.Id_Expediente)}
                                                onChange={() => handleSeleccionar(expediente.Id_Expediente)}
                                            />
                                        </TableCell>
                                        {lista.Tipo_Expediente === 2 ? (
                                            <>
                                                <TableCell>{expediente.Expediente_Adm || expediente.Autos || '-'}</TableCell>
                                                <TableCell sx={{ textTransform: 'capitalize' }}>{(expediente.Extracto || expediente.Caratula || '-').toLowerCase()}</TableCell>
                                                <TableCell>{expediente.Iniciador || '-'}</TableCell>
                                            </>
                                        ) : (
                                            <>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: expediente.Visto ? 400 : 600, textTransform: 'capitalize' }}>
                                                        {(expediente.Caratula || '-').toLowerCase()}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>{expediente.Autos || '-'}</TableCell>
                                            </>
                                        )}
                                        <TableCell>
                                            <Chip
                                                label={expediente.Estado || 'Sin estado'}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption">
                                                {formatDotNetDate(expediente.Fecha_Agregado, 'dd/MM/yyyy')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {editandoNotas[expediente.Id_Expediente] ? (
                                                <Box display="flex" gap={1} alignItems="center">
                                                    <TextField
                                                        size="small"
                                                        multiline
                                                        rows={2}
                                                        value={notasTemp[expediente.Id_Expediente] || ''}
                                                        onChange={(e) => setNotasTemp(prev => ({
                                                            ...prev,
                                                            [expediente.Id_Expediente]: e.target.value
                                                        }))}
                                                        style={{ width: 180 }}
                                                    />
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => handleGuardarNotas(expediente.Id_Expediente)}
                                                    >
                                                        <SaveIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleCancelarNotas(expediente.Id_Expediente)}
                                                    >
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            ) : (
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Typography variant="body2" sx={{
                                                        maxWidth: 200,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        {expediente.Notas || '-'}
                                                    </Typography>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleEditarNotas(expediente.Id_Expediente, expediente.Notas)}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            )}
                                        </TableCell>
                                        <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                                            <Tooltip title="Ver expediente">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => handleVerExpediente(expediente)}
                                                >
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Eliminar de la lista">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => {
                                                        setEliminarDialog({
                                                            open: true,
                                                            mensaje: '¿Eliminar este expediente de la lista?',
                                                            onConfirm: async () => {
                                                                setEliminarDialog(prev => ({ ...prev, open: false }));
                                                                try {
                                                                    await listasTrabajoService.eliminarExpedientes(lista.Id, [expediente.Id_Expediente]);
                                                                    setExpedientes(prev => prev.filter(e => e.Id_Expediente !== expediente.Id_Expediente));
                                                                    if (onExpedientesEliminados) onExpedientesEliminados(1);
                                                                    setSnackbar({ open: true, message: 'Expediente eliminado de la lista', severity: 'success' });
                                                                } catch (err) {
                                                                    setSnackbar({ open: true, message: 'Error: ' + err.message, severity: 'error' });
                                                                }
                                                            }
                                                        });
                                                    }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {usePagination && totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, py: 1.5 }}>
                            <Button
                                size="small"
                                disabled={page <= 1 || loading}
                                onClick={() => handlePageChange(page - 1)}
                            >
                                Anterior
                            </Button>
                            <Typography variant="body2" color="text.secondary">
                                Página {page} de {totalPages} ({totalExp} expedientes)
                            </Typography>
                            <Button
                                size="small"
                                disabled={page >= totalPages || loading}
                                onClick={() => handlePageChange(page + 1)}
                            >
                                Siguiente
                            </Button>
                        </Box>
                    )}
                    </>
                )}
            </DialogContent>

            <AccionesMasivasDialog
                open={dialogAccionesMasivas}
                onClose={() => setDialogAccionesMasivas(false)}
                /* Tomamos los datos del cache (acumula todas las páginas + el
                   "seleccionar todos"), no solo la página visible. */
                expedientes={seleccionados
                    .map(id => expedientesCacheRef.current.get(id))
                    .filter(Boolean)}
                tipoExpediente={lista?.Tipo_Expediente || 1}
                usuario={usuario}
                lista={lista}
                onSuccess={({ exitosos, errores, total }) => {
                    setSeleccionados([]);

                    if (onSuccess) {
                        onSuccess();
                    }
                }}
            />

        </Dialog>

        {/* Dialog confirmacion eliminar */}
        <Dialog
            open={eliminarDialog.open}
            onClose={() => setEliminarDialog(prev => ({ ...prev, open: false }))}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: '4px' } }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#d32f2f' }}>
                <WarningIcon /> Confirmar eliminacion
            </DialogTitle>
            <DialogContent>
                <Typography>{eliminarDialog.mensaje}</Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={() => setEliminarDialog(prev => ({ ...prev, open: false }))} color="inherit">
                    Cancelar
                </Button>
                <Button onClick={eliminarDialog.onConfirm} variant="contained" color="error">
                    Eliminar
                </Button>
            </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
            <MuiAlert
                elevation={6}
                variant="filled"
                severity={snackbar.severity}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            >
                {snackbar.message}
            </MuiAlert>
        </Snackbar>
        </>
    );
};

export default DetalleListaDialog;